(() => {
  const relativeReviewTimes = ['1 day ago', '2 days ago', '3 days ago', '4 days ago'];

  const relativeTimeFor = (index, total) => {
    const maxIndex = Math.max(0, Number(total || 1) - 1);
    const clamped = Math.min(Math.max(0, index), maxIndex);
    if (clamped < relativeReviewTimes.length) return relativeReviewTimes[clamped];
    const oneWeekStart = Math.max(7, Math.floor((maxIndex + 1) * .18));
    const twoWeeksStart = Math.max(oneWeekStart + 1, Math.floor((maxIndex + 1) * .82));
    if (clamped < oneWeekStart) return '6 days ago';
    if (clamped < twoWeeksStart) return '1 week ago';
    return '2 weeks ago';
  };

  const init = (root) => {
    if (!root || root.dataset.dsReviewsReady === 'true') return;
    root.dataset.dsReviewsReady = 'true';

    const rail = root.querySelector('[data-ds-review-rail]');
    if (!rail) return;

    const writeReviewButton = root.querySelector('[data-ds-write-review]');
    const collector = root.querySelector('[data-ds-judge-me-collector]');
    const reviewStatus = root.querySelector('[data-ds-review-status]');

    const nativeWriteReviewTrigger = () => {
      if (!collector) return null;
      const directTrigger = collector.querySelector('.jdgm-write-rev-link, [data-jdgm-write-review]');
      if (directTrigger) return directTrigger;
      return Array.from(collector.querySelectorAll('button, a')).find((candidate) => {
        return candidate.textContent?.trim().toLowerCase() === 'write a review';
      }) || null;
    };

    const syncLegacyFormVisibility = () => {
      if (!collector) return;
      const form = collector.querySelector('.jdgm-form-wrapper');
      const formIsOpen = Boolean(form && getComputedStyle(form).display !== 'none');
      collector.classList.toggle('is-legacy-form-open', formIsOpen);
    };

    if (collector) {
      new MutationObserver(syncLegacyFormVisibility).observe(collector, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    writeReviewButton?.addEventListener('click', () => {
      writeReviewButton.disabled = true;
      writeReviewButton.setAttribute('aria-busy', 'true');
      if (reviewStatus) reviewStatus.textContent = '';
      const startedAt = Date.now();
      const openNativeReviewForm = () => {
        const trigger = nativeWriteReviewTrigger();
        if (trigger) {
          trigger.click();
          writeReviewButton.disabled = false;
          writeReviewButton.removeAttribute('aria-busy');
          requestAnimationFrame(syncLegacyFormVisibility);
          return;
        }
        if (Date.now() - startedAt < 8000) {
          window.setTimeout(openNativeReviewForm, 160);
          return;
        }
        writeReviewButton.disabled = false;
        writeReviewButton.removeAttribute('aria-busy');
        if (reviewStatus) reviewStatus.textContent = 'The review form is temporarily unavailable. Please try again.';
      };
      openNativeReviewForm();
    });

    const cards = Array.from(rail.querySelectorAll('.ds-fixed-reviews__card'));
    cards.forEach((card, index) => {
      const date = card.querySelector('[data-ds-review-date]');
      if (date) date.textContent = relativeTimeFor(index, cards.length);
    });

    const cardDistance = () => {
      const card = rail.querySelector('.ds-fixed-reviews__card');
      const gap = Number.parseFloat(getComputedStyle(rail).columnGap || '0');
      return card ? card.getBoundingClientRect().width + gap : rail.clientWidth;
    };

    const loadAhead = async () => {
      const remaining = rail.scrollWidth - rail.scrollLeft - rail.clientWidth;
      const threshold = Math.max(rail.clientWidth, cardDistance() * 3);
      if (remaining > threshold || typeof root.dsLoadMoreReviews !== 'function') return;
      await root.dsLoadMoreReviews();
    };

    const goNext = async () => {
      await loadAhead();
      const distance = cardDistance();
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      if (rail.scrollLeft >= maxScroll - 2 && root.dsHasMoreReviews?.() !== true) {
        rail.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      rail.scrollBy({ left: distance, behavior: 'smooth' });
    };

    const autoplayDelay = 5000;
    let autoplayBusy = false;
    let autoplayTimer = null;
    let carouselIsVisible = false;

    const autoAdvance = async () => {
      if (autoplayBusy) return;
      autoplayBusy = true;
      root.dataset.dsReviewAutoplay = 'advancing';
      try {
        await goNext();
      } finally {
        autoplayBusy = false;
        root.dataset.dsReviewAutoplay = 'active';
      }
    };

    const stopAutoplay = () => {
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!carouselIsVisible || document.hidden) return;
      root.dataset.dsReviewAutoplay = 'active';
      autoplayTimer = window.setInterval(autoAdvance, autoplayDelay);
    };

    root.querySelector('[data-ds-review-prev]')?.addEventListener('click', () => {
      rail.scrollBy({ left: -cardDistance(), behavior: 'smooth' });
      startAutoplay();
    });

    root.querySelector('[data-ds-review-next]')?.addEventListener('click', async () => {
      await goNext();
      startAutoplay();
    });

    let autoplayObserver = null;
    if ('IntersectionObserver' in window) {
      autoplayObserver = new IntersectionObserver(([entry]) => {
        const wasVisible = carouselIsVisible;
        carouselIsVisible = Boolean(entry?.isIntersecting);
        if (carouselIsVisible && !wasVisible) {
          autoAdvance();
          startAutoplay();
        } else if (!carouselIsVisible) {
          stopAutoplay();
        }
      }, { threshold: [0] });
      autoplayObserver.observe(root);
    } else {
      carouselIsVisible = true;
      autoAdvance();
      startAutoplay();
    }

    const handleVisibilityChange = () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleSectionUnload = (event) => {
      if (event.target !== root.closest('.shopify-section')) return;
      stopAutoplay();
      autoplayObserver?.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('shopify:section:unload', handleSectionUnload);
    };
    document.addEventListener('shopify:section:unload', handleSectionUnload);

    const reviewBodyIsOverflowing = (body) => {
      if (!body || body.classList.contains('is-expanded')) return true;
      const collapsedHeight = body.getBoundingClientRect().height;
      body.classList.add('is-measuring');
      const fullHeight = body.getBoundingClientRect().height;
      body.classList.remove('is-measuring');
      return fullHeight > collapsedHeight + 2;
    };

    const updateMoreButton = (button) => {
      const body = button?.closest('.ds-fixed-reviews__content')?.querySelector('[data-ds-review-body]');
      if (!body || body.classList.contains('is-expanded')) return;
      button.hidden = !reviewBodyIsOverflowing(body);
    };

    const updateMoreButtons = () => {
      root.querySelectorAll('[data-ds-review-more]').forEach(updateMoreButton);
    };

    const scheduleMoreButtonUpdate = () => {
      requestAnimationFrame(() => {
        updateMoreButtons();
        requestAnimationFrame(updateMoreButtons);
      });
    };

    rail.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ds-review-more]');
      if (!button) return;
      const body = button.closest('.ds-fixed-reviews__content')?.querySelector('[data-ds-review-body]');
      if (!body) return;
      const expanded = body.classList.toggle('is-expanded');
      button.textContent = expanded ? 'Less' : 'More';
      if (expanded) button.hidden = false;
      else requestAnimationFrame(() => updateMoreButton(button));
    });

    scheduleMoreButtonUpdate();
    window.addEventListener('load', scheduleMoreButtonUpdate, { once: true });
    window.addEventListener('resize', updateMoreButtons, { passive: true });
    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleMoreButtonUpdate).catch(() => {});
    }
    root.addEventListener('ds:reviews-appended', scheduleMoreButtonUpdate);
  };

  document.querySelectorAll('[data-ds-fixed-reviews]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-ds-fixed-reviews]').forEach(init);
  });
})();
